/**
 * Team Hooks - React Query hooks for team operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../utils/apiError';

// Query keys
export const teamKeys = {
  all: ['teams'] as const,
  list: () => [...teamKeys.all, 'list'] as const,
};

export interface Team {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
}

/**
 * Get all teams
 */
export const useTeams = () => {
  return useQuery({
    queryKey: teamKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: { teams: Team[] };
      }>('/users/teams');
      if (response.data.success) {
        return response.data.data.teams;
      }
      return [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Create team
 */
export const useCreateTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, description }: { name: string; description?: string }) => {
      const response = await apiClient.post('/users/teams', { name, description });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.list() });
      toast.success('Team created successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to create team'));
    },
  });
};

/**
 * Update team
 */
export const useUpdateTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, name, description }: { teamId: string; name?: string; description?: string }) => {
      const response = await apiClient.patch(`/users/teams/${teamId}`, { name, description });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.list() });
      toast.success('Team updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to update team'));
    },
  });
};

export interface TeamMember {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string | null;
  department: string;
}

/**
 * Get team members
 */
export const useTeamMembers = (teamId: string) => {
  return useQuery({
    queryKey: [...teamKeys.all, teamId, 'members'] as const,
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: { members: TeamMember[] };
      }>(`/users/teams/${teamId}/members`);
      if (response.data.success) {
        return response.data.data.members;
      }
      return [];
    },
    enabled: !!teamId,
    staleTime: 1000 * 60 * 2,
  });
};

/**
 * Delete team
 */
export const useDeleteTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamId: string) => {
      const response = await apiClient.delete(`/users/teams/${teamId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.list() });
      toast.success('Team deleted successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete team'));
    },
  });
};
